import { useEffect, useRef, useState } from 'react'

export interface UseInViewOptions {
  /** Fraction of the element that must be visible before it triggers. */
  threshold?: number
  /** Margin around the viewport. Negative bottom triggers a little early. */
  rootMargin?: string
  /** Keep the element revealed after it first enters the viewport. */
  once?: boolean
}

/**
 * Tracks whether an element has scrolled into the viewport.
 * Powers the scroll-reveal animations across the site.
 * Respects `prefers-reduced-motion` by revealing immediately.
 */
export function useInView<T extends HTMLElement = HTMLElement>({
  threshold = 0.1,
  rootMargin = '0px 0px -6% 0px',
  once = true,
}: UseInViewOptions = {}) {
  const ref = useRef<T | null>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    // Users who prefer reduced motion see content immediately, no animation.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setInView(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          if (once) observer.disconnect()
        } else if (!once) {
          setInView(false)
        }
      },
      { threshold, rootMargin },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [threshold, rootMargin, once])

  return { ref, inView }
}
